/**
 * ConfidenceTrendSparkline
 *
 * Lightweight 30-day sparkline rendered with SVG `<polyline>`.
 * An area fill sits below the line.  Stroke color is determined by
 * the overall trend direction:
 *   - up   -> green  (#22C55E)
 *   - down -> red    (#EF4444)
 *   - flat -> gray   (neutral foreground)
 */
import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { semanticColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Trend detection                                                    */
/* ------------------------------------------------------------------ */

type Trend = 'up' | 'down' | 'flat';

function detectTrend(data: number[]): Trend {
  if (data.length < 2) return 'flat';

  // Compare average of last third vs first third
  const third = Math.max(1, Math.floor(data.length / 3));
  const firstAvg = data.slice(0, third).reduce((a, b) => a + b, 0) / third;
  const lastAvg = data.slice(-third).reduce((a, b) => a + b, 0) / third;
  const delta = lastAvg - firstAvg;

  if (delta > 2) return 'up';
  if (delta < -2) return 'down';
  return 'flat';
}

// #EF4444 (red) and #9CA3AF (grey) are not in brand-config.json's palette —
// no token represents them, so they are left as raw literals.
function trendColor(trend: Trend): string {
  switch (trend) {
    case 'up': return semanticColors.success;
    case 'down': return '#EF4444';
    case 'flat': return '#9CA3AF';
  }
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  root: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
});

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ConfidenceTrendSparklineProps {
  /** Array of score values (0-100), one per day. */
  data: number[];
  /** SVG width in pixels. @default 120 */
  width?: number;
  /** SVG height in pixels. @default 32 */
  height?: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ConfidenceTrendSparkline: React.FC<ConfidenceTrendSparklineProps> = ({
  data,
  width = 120,
  height = 32,
}) => {
  const styles = useStyles();

  if (data.length === 0) {
    return <span className={styles.root} style={{ width, height }} />;
  }

  // Normalize data points to SVG coordinate space
  const padding = 2;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1; // avoid divide-by-zero

  const points = data.map((v, i) => {
    const x = padding + (i / Math.max(1, data.length - 1)) * innerW;
    const y = padding + innerH - ((v - min) / range) * innerH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const polylinePoints = points.join(' ');

  // Area polygon: close path along the bottom edge
  const areaPoints = [
    ...points,
    `${(padding + innerW).toFixed(1)},${(padding + innerH).toFixed(1)}`,
    `${padding.toFixed(1)},${(padding + innerH).toFixed(1)}`,
  ].join(' ');

  const trend = detectTrend(data);
  const color = trendColor(trend);

  return (
    <span
      className={styles.root}
      role="img"
      aria-label={`Confidence trend: ${trend}`}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Area fill */}
        <polygon
          points={areaPoints}
          fill={color}
          fillOpacity={0.12}
        />
        {/* Line */}
        <polyline
          points={polylinePoints}
          stroke={color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </span>
  );
};
